// client/src/components/admin/MessageManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './Manager.css';
import './ProductManager.css';

const MessageViewer = ({ message, onCancel }) => {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Message from {message.name}</h2>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Sender Email</label>
            <p>
              <a href={`mailto:${message.email}`}>{message.email}</a>
            </p>
          </div>
          <div className="form-group">
            <label>Subject</label>
            <p>
              <strong>{message.subject}</strong>
            </p>
          </div>
          <div className="form-group">
            <label>Message Content</label>
            <p className="message-body">{message.message}</p>
          </div>
        </div>
        <div className="modal-actions">
          <a
            href={`mailto:${message.email}?subject=${encodeURIComponent(
              `Re: ${message.subject}`
            )}`}
            className="btn-save"
            style={{ textDecoration: 'none' }}
          >
            <i className="fas fa-reply"></i> Reply via Email
          </a>
          <button type="button" onClick={onCancel} className="btn-cancel">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (err) {
      setError('Failed to fetch messages. Please try refreshing the page.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this message?')) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/messages/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchMessages();
      } catch (err) {
        alert('Failed to delete message. Please try again.');
        console.error(err);
      }
    }
  };

  const filteredMessages = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return messages;
    return messages.filter(
      (msg) =>
        (msg.name && msg.name.toLowerCase().includes(term)) ||
        (msg.email && msg.email.toLowerCase().includes(term)) ||
        (msg.subject && msg.subject.toLowerCase().includes(term)) ||
        (msg.message && msg.message.toLowerCase().includes(term))
    );
  }, [messages, searchQuery]);

  const renderContent = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="5" className="admin-empty-cell">
            <i className="fas fa-circle-notch fa-spin"></i>
            Loading visitor messages...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td colSpan="5" className="error-message">
            {error}
          </td>
        </tr>
      );
    }
    if (filteredMessages.length === 0) {
      return (
        <tr>
          <td colSpan="5" className="admin-empty-cell">
            <i className="fas fa-envelope-open"></i>
            No messages found.
          </td>
        </tr>
      );
    }
    return filteredMessages.map((msg) => (
      <tr key={msg.id}>
        <td>
          <span className="cell-primary-text">
            {new Date(msg.created_at).toLocaleDateString()}
          </span>
        </td>
        <td>
          <span className="cell-primary-text">{msg.name}</span>
        </td>
        <td>
          <div className="contact-links-stack">
            <a href={`mailto:${msg.email}`}>
              <i className="far fa-envelope"></i> {msg.email}
            </a>
          </div>
        </td>
        <td>
          <span className="cell-primary-text">{msg.subject}</span>
        </td>
        <td>
          <div className="actions-cell">
            <button onClick={() => setSelectedMessage(msg)} className="btn-edit">
              <i className="fas fa-eye"></i> View
            </button>
            <button onClick={() => handleDelete(msg.id)} className="btn-delete">
              <i className="fas fa-trash-alt"></i> Delete
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="manager-container">
      {selectedMessage && (
        <MessageViewer
          message={selectedMessage}
          onCancel={() => setSelectedMessage(null)}
        />
      )}

      <div className="manager-page-header">
        <div className="manager-title-group">
          <h1>Visitor Messages</h1>
          <p>Messages and inquiries submitted through the main website contact form.</p>
        </div>
        <div className="manager-header-actions">
          <button type="button" className="btn-refresh" onClick={fetchMessages}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      {/* --- KPI Summary Cards --- */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <div className="kpi-icon-box blue">
            <i className="fas fa-inbox"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Total Messages</span>
            <span className="kpi-value">{messages.length}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box green">
            <i className="fas fa-search"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Showing Results</span>
            <span className="kpi-value">{filteredMessages.length}</span>
          </div>
        </div>
      </div>

      {/* --- Search Toolbar --- */}
      <div className="admin-toolbar">
        <div className="admin-search-box">
          <i className="fas fa-search"></i>
          <input
            type="search"
            placeholder="Search messages by sender name, email, or subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="manager-table-wrapper">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Sender</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{renderContent()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default MessageManager;
